<template>
  <div class="mb-3">
    <label :for="inputId" class="form-label">
      社員印画像 <span class="badge text-bg-secondary">任意</span>
    </label>
    <input
      :id="inputId"
      ref="fileInput"
      class="form-control"
      type="file"
      accept="image/png,image/jpeg"
      @change="handleFileChange"
    >
    <div class="form-text">
      PNGまたはJPEG形式、2MB以下の正方形画像を推奨します。未登録の場合、見積書に社員印は表示されません。
    </div>
    <div v-if="previewUrl" class="employee-seal-preview mt-2">
      <img :src="previewUrl" alt="社員印画像プレビュー">
      <button type="button" class="btn btn-sm btn-outline-danger" @click="clearImage">
        画像を削除
      </button>
    </div>
    <div v-if="displayError" class="alert alert-danger mt-2">{{ displayError }}</div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const SUPPORTED_TYPES = ['image/png', 'image/jpeg'];

const props = defineProps({
  imageFile: {
    type: Object,
    default: null
  },
  existingImageUrl: {
    type: String,
    default: ''
  },
  removeImage: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  },
  inputId: {
    type: String,
    default: 'employeeSealImage'
  }
});

const emit = defineEmits([
  'update:imageFile',
  'update:removeImage',
  'validation-error'
]);
const fileInput = ref(null);
const objectUrl = ref('');
const localError = ref('');
const previewUrl = computed(() => {
  if (objectUrl.value) return objectUrl.value;
  return props.removeImage ? '' : props.existingImageUrl;
});
const displayError = computed(() => localError.value || props.errorMessage);

const releaseObjectUrl = () => {
  if (!objectUrl.value) return;
  URL.revokeObjectURL(objectUrl.value);
  objectUrl.value = '';
};

const setValidationError = (message) => {
  localError.value = message;
  emit('validation-error', message);
};

const handleFileChange = (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!SUPPORTED_TYPES.includes(file.type)) {
    event.target.value = '';
    setValidationError('社員印画像はPNGまたはJPEG形式を指定してください。');
    return;
  }
  if (file.size > MAX_FILE_SIZE) {
    event.target.value = '';
    setValidationError('社員印画像は2MB以下にしてください。');
    return;
  }

  releaseObjectUrl();
  objectUrl.value = URL.createObjectURL(file);
  setValidationError('');
  emit('update:imageFile', file);
  emit('update:removeImage', false);
};

const clearImage = () => {
  releaseObjectUrl();
  if (fileInput.value) fileInput.value.value = '';
  setValidationError('');
  emit('update:imageFile', null);
  emit('update:removeImage', Boolean(props.existingImageUrl));
};

watch(() => props.imageFile, (file) => {
  if (!file) releaseObjectUrl();
});

onBeforeUnmount(releaseObjectUrl);
</script>

<style scoped>
.employee-seal-preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.employee-seal-preview img {
  display: block;
  width: 80px;
  height: 80px;
  padding: 4px;
  border: 1px solid #ced4da;
  background: #fff;
  object-fit: contain;
}
</style>
